/**
 * Web push (FCM) on the client — permission, device token, settings sync, foreground display.
 * Server side: app/api/routes/push.py. Plan: docs/MY_TASKS/TASK_PUSH_NOTIFICATIONS.md.
 *
 * - Permission is only ever requested from a tap (iOS rejects anything else).
 * - The token is registered against the existing CRA service worker — no second worker.
 * - Tokens are re-registered at most weekly on app start so the server's lastSeenAt stays fresh.
 */
import { deleteToken, getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';
import { firebaseApp } from '../auth/firebase';
import { apiFetch } from '../api/authApi';
import { getApiBaseUrl } from '../api/apiBase';
import { beginWork, endWork } from '../utils/appUpdate';

const API = getApiBaseUrl();
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;
const LAST_REGISTERED_KEY = 'kuroco_push_registered_at';
const REFRESH_EVERY_MS = 7 * 24 * 3600 * 1000;

async function api(path, options = {}) {
  const res = await apiFetch(`${API}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `${res.status} ${res.statusText}`);
  return data;
}

function isIos() {
  return /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function isStandalone() {
  return window.matchMedia?.('(display-mode: standalone)').matches || navigator.standalone === true;
}

/** { ok: true } or { ok: false, reason } — reason is shown under the toggle as is. */
export async function pushSupport() {
  if (typeof window === 'undefined') return { ok: false, reason: '' };
  if (isIos() && !isStandalone()) {
    return {
      ok: false,
      reason: 'iPhoneでは、Safariの共有ボタンから「ホーム画面に追加」し、ホーム画面のアイコンから開くと通知を受け取れます。',
    };
  }
  if (!('serviceWorker' in navigator) || !('Notification' in window) || !(await isSupported().catch(() => false))) {
    return { ok: false, reason: 'このブラウザは通知に対応していません。' };
  }
  if (!VAPID_KEY) return { ok: false, reason: '通知の設定が完了していません（管理者に連絡してください）。' };
  return { ok: true };
}

async function swRegistration() {
  // The CRA worker registers on page load (production builds only); don't hang if it never does.
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('通知の準備ができていません。ページを再読み込みしてからもう一度お試しください。')), 10000)
    ),
  ]);
}

async function deviceToken() {
  const serviceWorkerRegistration = await swRegistration();
  return getToken(getMessaging(firebaseApp), { vapidKey: VAPID_KEY, serviceWorkerRegistration });
}

// While the app is on screen FCM hands a push to the page instead of the worker, so the page
// must show it itself. Registered once — at app start if permission is already granted, or the
// moment push is turned on (otherwise a push arriving in that same session is silently dropped).
let foregroundListening = false;
function listenForeground() {
  if (foregroundListening) return;
  foregroundListening = true;
  onMessage(getMessaging(firebaseApp), async (payload) => {
    const n = payload?.notification || {};
    const reg = await swRegistration().catch(() => null);
    reg?.showNotification(n.title || 'KUROCO', { body: n.body || '', icon: '/icon-192-v2.png', data: payload?.fcmOptions });
  });
}

export function fetchPushSettings() {
  return api('/me/notifications');
}

/**
 * Register this device for push (確認依頼 and, if on, the daily reminder).
 * `reminder` / `time` are saved as given, so turning push on doesn't silently flip the reminder.
 * MUST be called directly from a tap handler.
 */
export async function enablePush({ reminder, time }) {
  beginWork(); // the permission prompt + token registration must not be cut by an app-update reload
  try {
    return await enablePushInner({ reminder, time });
  } finally {
    endWork();
  }
}

async function enablePushInner({ reminder, time }) {
  // Safari only shows the prompt when requestPermission() runs straight from the tap — any
  // await before it (e.g. isSupported()) can lose the gesture. Sync checks first, prompt, then the rest.
  if (isIos() && !isStandalone()) throw new Error((await pushSupport()).reason);
  if (!('Notification' in window) || !('serviceWorker' in navigator)) throw new Error('このブラウザは通知に対応していません。');
  if (!VAPID_KEY) throw new Error('通知の設定が完了していません（管理者に連絡してください）。');
  const permissionPromise = Notification.requestPermission();
  const support = await pushSupport();
  if (!support.ok) throw new Error(support.reason);
  const permission = await permissionPromise;
  if (permission !== 'granted') {
    throw new Error(
      permission === 'denied'
        ? 'このブラウザで通知がブロックされています。サイトの設定から通知を許可してください。'
        : '通知が許可されませんでした。'
    );
  }
  const token = await deviceToken();
  listenForeground();
  const saved = await api('/me/notifications', {
    method: 'PUT',
    body: JSON.stringify({ enabled: Boolean(reminder), time, token, userAgent: navigator.userAgent }),
  });
  try { localStorage.setItem(LAST_REGISTERED_KEY, String(Date.now())); } catch { /* private mode */ }
  return saved;
}

/** Remove this device's token (server + FCM). Settings are left alone. */
export async function forgetThisDevice() {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted' || !VAPID_KEY) return;
  try {
    const token = await deviceToken();
    if (token) {
      await api('/me/notifications/token', { method: 'DELETE', body: JSON.stringify({ token }) });
      await deleteToken(getMessaging(firebaseApp));
    }
  } catch {
    // Best effort — a token that stays behind is removed server-side once FCM reports it dead.
  }
  try { localStorage.removeItem(LAST_REGISTERED_KEY); } catch { /* private mode */ }
}

/** Stop all pushes on this device. The reminder is switched off too: it could not arrive anyway. */
export async function disablePush(time) {
  await forgetThisDevice();
  return api('/me/notifications', { method: 'PUT', body: JSON.stringify({ enabled: false, time }) });
}

/** Daily reminder on/off + time only. Device registration is untouched. */
export function saveReminder({ enabled, time }) {
  return api('/me/notifications', { method: 'PUT', body: JSON.stringify({ enabled: Boolean(enabled), time }) });
}

export function sendTestPush() {
  return api('/me/notifications/test', { method: 'POST' });
}

/** KUROCO からのお知らせを全員に送る（dev ロールのみ。サーバーが 403 で弾く）。 */
export function sendBroadcast({ title, body, path = '/settings/updates' }) {
  return api('/push/broadcast', { method: 'POST', body: JSON.stringify({ title, body, path }) });
}

/** App start: keep the server's lastSeenAt fresh (weekly), and show pushes that arrive while the app is open. */
export async function startPush() {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted' || !VAPID_KEY) return;
  if (!(await isSupported().catch(() => false))) return;

  listenForeground();

  let last = 0;
  try { last = Number(localStorage.getItem(LAST_REGISTERED_KEY) || 0); } catch { /* private mode */ }
  if (Date.now() - last < REFRESH_EVERY_MS) return;
  try {
    const settings = await fetchPushSettings();
    // Refresh whenever this user uses push at all: a manager may have the reminder off
    // and still need 確認依頼 pushes.
    if (!settings?.devices && !settings?.enabled) return;
    const token = await deviceToken();
    await api('/me/notifications', {
      method: 'PUT',
      body: JSON.stringify({ enabled: Boolean(settings.enabled), time: settings.time, token, userAgent: navigator.userAgent }),
    });
    localStorage.setItem(LAST_REGISTERED_KEY, String(Date.now()));
  } catch {
    // Try again next start.
  }
}
